'use client';

import { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Users, ChevronDown, Minus, Plus, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRouter } from 'next/navigation';
import { areaAPI } from '@/lib/api';

// Date Range Picker Component
interface Destination {
    icon: string;
    title: string;
    desc: string;
    externalId?: string;
}

function DateRangePickerContent({ date, setDate }: { date: DateRange | undefined, setDate: (date: DateRange | undefined) => void }) {
    const [activeTab, setActiveTab] = useState('dates');

    const quickSelectOptions = [
        { label: 'Exact dates', days: 0 },
        { label: '± 1 day', days: 1 },
        { label: '± 2 days', days: 2 },
        { label: '± 3 days', days: 3 },
        { label: '± 7 days', days: 7 },
        { label: '± 14 days', days: 14 },
    ];

    const handleReset = () => {
        setDate(undefined);
    };

    const handleQuickSelect = (days: number) => {
        const today = new Date();
        const endDate = new Date();
        endDate.setDate(today.getDate() + days);

        if (days === 0) {
            setDate({ from: today, to: today });
        } else {
            setDate({ from: today, to: endDate });
        }
    };

    return (
        <div className="w-auto bg-white rounded-lg shadow-lg border border-gray-200">
            {/* Tabs */}
            {/* <div className="flex items-center justify-center p-2 border-b border-gray-100">
                <div className="flex bg-gray-100 rounded-full p-1">
                    {['Dates', 'Months', 'Flexible'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab.toLowerCase())}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                activeTab === tab.toLowerCase()
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div> */}

            {/* Calendar Content */}
            <div className="p-4">
                <div className="flex justify-between gap-4">
                    <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={date?.from || new Date()}
                        selected={date}
                        onSelect={setDate}
                        numberOfMonths={2}
                        disabled={(date) => date < new Date()}
                        className="rounded-md w-full"
                    />
                </div>

                {/* Quick Select Options */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2 mb-4">
                        {quickSelectOptions.map((option) => (
                            <button
                                key={option.label}
                                onClick={() => handleQuickSelect(option.days)}
                                className="px-3 py-1.5 text-sm border border-gray-300 rounded-full hover:border-gray-400 hover:bg-gray-50 transition-colors"
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>

                    {/* Reset Button */}
                    <div className="flex justify-end">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleReset}
                            className="text-gray-600 hover:text-gray-900"
                        >
                            Reset
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function SearchFilter() {
    const router = useRouter();
    // Guest counts
    const [adults, setAdults] = useState(0);
    const [children, setChildren] = useState(0);
    const [infants, setInfants] = useState(0);
    const [pets, setPets] = useState(0);

    // Selected dates
    const [date, setDate] = useState<DateRange | undefined>();

    // Selected destination
    const [selectedDestination, setSelectedDestination] = useState('');

    const searchFilterRef = useRef<HTMLDivElement>(null);

    // Handle search
    const handleSearch = () => {
        if (adults === 0) {
            alert('Please select at least one adult guest');
            return;
        }

        if (!selectedDestination) {
            alert('Please select a destination');
            return;
        }

        if (!date?.from) {
            alert('Please select check-in date');
            return;
        }

        console.log('Searching for:', {
            destination: selectedDestination,
            checkIn: date.from,
            checkOut: date.to,
            guests: { adults, children, infants, pets }
        });
        // Here you would typically make an API call or navigate to search results
        // alert(`Searching for ${selectedDestination} from ${date.from ? format(date.from, 'PPP') : 'N/A'} to ${date.to ? format(date.to, 'PPP') : 'N/A'} with ${adults + children + infants} guests`);
        router.push(`/listings?area=${selectedDestination}&checkIn=${date.from ? format(date.from, 'yyyy-MM-dd') : ''}&checkOut=${date.to ? format(date.to, 'yyyy-MM-dd') : ''}&guests=${adults + children + infants}`);
    };

    const [suggestedDestinations, setSuggestedDestinations] = useState<Destination[]>([
        { icon: '🧭', title: 'Nearby', desc: 'Find what\'s around you' }
    ]);

    useEffect(() => {
        const fetchAreas = async () => {
            try {
                const response = await areaAPI.getFrontendList({ is_show_on_home: true });
                if (response.success && response.data) {
                    const apiDestinations = response?.data?.data?.map((area: any) => ({
                        icon: '🏙️', // Default icon for API areas
                        title: area.name,
                        desc: area.short_description || `Explore ${area.name}`,
                        externalId: area.externalId || area._id || area.name // Fallback to name if ID missing
                    }));
                    setSuggestedDestinations([
                        // { icon: '🧭', title: 'Nearby', desc: 'Find what\'s around you' },
                        ...apiDestinations
                    ]);
                }
            } catch (error) {
                console.error('Failed to fetch areas:', error);
            }
        };

        fetchAreas();
    }, []);

    return (
        <div className="w-full max-w-6xl mx-auto px-4 rounded-xl" ref={searchFilterRef}>
            {/* Dark gray container */}
            <div className="bg-black/60 shadow-lg border border-gray-700 p-8 rounded-xl">
                {/* White input field container */}
                <div className="bg-white rounded-lg px-2 flex items-center gap-1">
                    {/* Where */}
                    <div className="relative flex-1">
                        <Select value={selectedDestination} onValueChange={setSelectedDestination}>
                            <SelectTrigger className="border-0 shadow-none hover:bg-gray-50 h-auto py-3">
                                <div className="flex items-center gap-2 w-full">
                                    <MapPin className="text-gray-500" size={16} />
                                    <div className="text-left flex-1">
                                        <div className="text-xs text-gray-500">Where</div>
                                        <SelectValue placeholder="Search destinations" className="text-sm font-medium text-gray-900" />
                                    </div>
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectLabel>Suggested destinations</SelectLabel>
                                    {suggestedDestinations.map((dest, index) => (
                                        <SelectItem key={index} value={dest.externalId || dest.title}>
                                            <div className="flex items-center gap-2">
                                                <span className="text-lg">{dest.icon}</span>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{dest.title}</div>
                                                    {/* <div className="text-xs text-gray-500">{dest.desc}</div> */}
                                                </div>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Separator */}
                    <div className="w-px h-10 bg-gray-800 mx-1"></div>

                    {/* Date Range */}
                    <div className="relative flex-1">
                        <Popover>
                            <PopoverTrigger asChild>
                                <div className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                                    <CalendarIcon className="text-gray-500" size={16} />
                                    <div className="text-left">
                                        <div className="text-xs text-gray-500">Dates</div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {date?.from ? (
                                                date.to ? (
                                                    <>
                                                        {format(date.from, "LLL dd")} - {format(date.to, "LLL dd")}
                                                    </>
                                                ) : (
                                                    format(date.from, "LLL dd")
                                                )
                                            ) : (
                                                "Add dates"
                                            )}
                                        </div>
                                    </div>
                                    <ChevronDown className="text-gray-400 ml-auto" size={16} />
                                </div>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 border-0" align="start">
                                <DateRangePickerContent
                                    date={date}
                                    setDate={setDate}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    {/* Separator */}
                    <div className="w-px h-10 bg-gray-800 mx-1"></div>

                    {/* Who */}
                    <div className="relative flex-1">
                        <Popover>
                            <PopoverTrigger asChild>
                                <div className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                                    <Users className="text-gray-500" size={16} />
                                    <div className="text-left">
                                        <div className="text-xs text-gray-500">Who</div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {adults + children + infants > 0
                                                ? `${adults + children + infants} guest${adults + children + infants > 1 ? 's' : ''}`
                                                : 'Add guests'
                                            }
                                        </div>
                                    </div>
                                    <ChevronDown className="text-gray-400 ml-auto" size={16} />
                                </div>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 p-4" align="start">
                                <div className="space-y-4">
                                    {/* Adults */}
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">Adults</div>
                                            <div className="text-xs text-gray-500">Ages 13 or above</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => setAdults(Math.max(0, adults - 1))}
                                                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                                            >
                                                <Minus size={16} className="text-gray-600" />
                                            </button>
                                            <span className="w-8 text-center text-gray-900">{adults}</span>
                                            <button
                                                disabled={adults >= 2}
                                                onClick={() => setAdults(adults + 1)}
                                                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                                            >
                                                <Plus size={16} className="text-gray-600" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Children */}
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">Children</div>
                                            <div className="text-xs text-gray-500">Ages 2-12</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => setChildren(Math.max(0, children - 1))}
                                                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                                            >
                                                <Minus size={16} className="text-gray-600" />
                                            </button>
                                            <span className="w-8 text-center text-gray-900">{children}</span>
                                            <button
                                                disabled={children >= 2}
                                                onClick={() => setChildren(children + 1)}
                                                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                                            >
                                                <Plus size={16} className="text-gray-600" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Infants */}
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">Infants</div>
                                            <div className="text-xs text-gray-500">Under 2</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => setInfants(Math.max(0, infants - 1))}
                                                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                                            >
                                                <Minus size={16} className="text-gray-600" />
                                            </button>
                                            <span className="w-8 text-center text-gray-900">{infants}</span>
                                            <button
                                                disabled={infants >= 2}
                                                onClick={() => setInfants(infants + 1)}
                                                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                                            >
                                                <Plus size={16} className="text-gray-600" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Pets */}
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">Pets</div>
                                            <div className="text-xs text-gray-500">Service animals allowed</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => setPets(Math.max(0, pets - 1))}
                                                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                                            >
                                                <Minus size={16} className="text-gray-600" />
                                            </button>
                                            <span className="w-8 text-center text-gray-900">{pets}</span>
                                            <button
                                                disabled={pets >= 2}
                                                onClick={() => setPets(pets + 1)}
                                                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                                            >
                                                <Plus size={16} className="text-gray-600" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="pt-2 border-t border-gray-200">
                                        <div className="text-xs text-gray-500 text-center">
                                            Click outside to close
                                        </div>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>

                    {/* Separator */}
                    <div className="w-px h-10 bg-gray-800 mx-1"></div>

                    {/* Search Button */}
                    <Button
                        className="text-white rounded h-full"
                        onClick={handleSearch}
                    >
                        <Search size={16} />
                        {/* Search */}
                    </Button>
                </div>
            </div>
        </div>
    );
}
